import type { ITuningClock, TuningIdentity } from '@playcastdotio/PeerCore';

export type TrialReplacementLeaseOptions = {
  clock: ITuningClock;
  retain: (target: TuningIdentity) => void;
  release: (target: TuningIdentity, outcome: 'connected' | 'failed' | 'terminal') => void;
};
type RetainedTrial = { target: TuningIdentity; startedAtMs: number; expiresAtMs: number; timer: ReturnType<typeof setTimeout> };

/** Planned media ownership is separate from normal reconnect holds and player departure. */
export class TrialReplacementLease {
  private readonly retained = new Map<string, RetainedTrial>();
  constructor(private readonly options: TrialReplacementLeaseOptions) {}

  begin(target: TuningIdentity): void {
    if (this.holds(target.peerId)) throw new Error('A planned replacement already owns this player');
    const now = this.options.clock.monotonicMs();
    this.options.retain(target);
    // The operation owner bounds attempt one at 15s; retain the seat through its one restore.
    this.install({ ...target }, now, now + 30_000);
  }

  extendForRestore(target: TuningIdentity): void {
    const previous = this.retained.get(target.peerId);
    if (!previous || previous.target.sessionId !== target.sessionId) { this.begin(target); return; }
    clearTimeout(previous.timer);
    this.install({ ...target }, previous.startedAtMs,
      Math.min(previous.startedAtMs + 30_000, this.options.clock.monotonicMs() + 15_000));
  }

  holds(peerId: string): boolean {
    const lease = this.retained.get(peerId);
    if (!lease) return false;
    if (this.options.clock.monotonicMs() >= lease.expiresAtMs) { this.complete(lease.target, 'failed'); return false; }
    return true;
  }

  complete(target: TuningIdentity, outcome: 'connected' | 'failed' | 'terminal'): void {
    const lease = this.retained.get(target.peerId);
    if (!lease || lease.target.trialId !== target.trialId || lease.target.connectionEpoch !== target.connectionEpoch
      || lease.target.sessionId !== target.sessionId) return;
    clearTimeout(lease.timer); this.retained.delete(target.peerId); this.options.release(lease.target, outcome);
  }

  dispose(): void {
    for (const lease of [...this.retained.values()]) this.complete(lease.target, 'terminal');
  }

  private install(target: TuningIdentity, startedAtMs: number, expiresAtMs: number): void {
    const timer = setTimeout(() => this.complete(target, 'failed'), Math.max(0, expiresAtMs - this.options.clock.monotonicMs()));
    this.retained.set(target.peerId, { target, startedAtMs, expiresAtMs, timer });
  }
}
